import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { Card, Tag, Typography, DatePicker, Select, Switch, Space, Row, Col, theme, Button, Tooltip, Descriptions, Image, EmptyState, message, Modal, Form, Input, Popconfirm, Tabs, Badge, Checkbox, Segmented, Table, Drawer, Tag as TwTag, IconButton, Tooltip as TwTooltip } from '../../ui';
import { Eye, MapPin, AlertTriangle, CheckCircle2, Clock, Users, Plus, RefreshCw, UserX, CalendarDays, CalendarClock, Trash2 } from 'lucide-react';
import dayjs, { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { usePermission } from '../../store/auth';
import { TwDataTable as DataTable, type DataTableColumn } from '../../components/TwDataTable';
import { RegularizationReviewCard } from './regularization';
import { HolidaysCard } from './HolidaysCard';
import { AttendanceUserDrawer, type AttUser } from './AttendanceUserDrawer';
import { GrantCoverageModal } from '../territory/GrantCoverageModal';
import { useBadges } from '../../hooks/useBadges';
import { formatIst, formatIstTime } from '../../lib/time';
import { useReverseGeocode } from '../../lib/reverseGeocode';

const STATUS_OPTIONS = ['PRESENT', 'HALF_DAY', 'LEAVE', 'ABSENT', 'HOLIDAY'];

const STATUS_COLOR: Record<string, 'green' | 'gold' | 'blue' | 'red' | 'neutral'> = {
  PRESENT: 'green', HALF_DAY: 'gold', LEAVE: 'blue', ABSENT: 'red', HOLIDAY: 'neutral',
};
// How the record was created (SELF needs no chip).
const SOURCE_TAG: Record<string, { text: string; color: 'purple' | 'blue' }> = {
  REGULARIZED: { text: 'Regularized', color: 'purple' },
  MANUAL: { text: 'Manual', color: 'blue' },
};
const mapsUrl = (lat?: string | null, lng?: string | null) =>
  lat && lng ? `https://www.google.com/maps?q=${lat},${lng}` : undefined;
const fmtWorked = (min?: number | null) =>
  min == null ? '—' : `${Math.floor(min / 60)}h ${min % 60}m`;

/**
 * "Last location" table cell — reverse-geocodes the rep's latest activity coords into a full,
 * human-readable address (cached per-coordinate, so each place is looked up only once). Shows the
 * activity type + time as a subline and links straight to Google Maps. Falls back to a plain map
 * link (never bare lat/lng) if the geocode is unavailable.
 */
function LastLocationCell({ lat, lng, type, at }: { lat?: string | null; lng?: string | null; type?: string | null; at?: string | null }) {
  const geo = useReverseGeocode(lat, lng);
  const url = mapsUrl(lat, lng);
  if (!url) return <span className="text-muted">—</span>;
  const meta = `${type ?? 'Location'}${at ? ` · ${formatIstTime(at)}` : ''}`;
  return (
    <a
      href={url} target="_blank" rel="noreferrer" title={meta}
      className="group inline-flex max-w-[280px] items-start gap-1.5 text-brand-700! hover:text-brand-800! dark:text-brand-400!"
    >
      <MapPin size={14} className="mt-0.5 shrink-0" />
      <span className="min-w-0">
        <span className="line-clamp-2 text-[13px] font-medium leading-snug group-hover:underline">
          {geo.isFetching ? <span className="text-muted">Resolving address…</span> : (geo.data ?? 'View on map')}
        </span>
        {at && <span className="mt-0.5 block truncate text-[11px] text-subtle">{meta}</span>}
      </span>
    </a>
  );
}

function Kpi({ title, value, color, icon, onClick, active }: { title: string; value: React.ReactNode; color: string; icon: React.ReactNode; onClick?: () => void; active?: boolean }) {
  return (
    <div
      onClick={onClick}
      className={`rounded-card border border-line bg-surface shadow-card${onClick ? ' cursor-pointer transition duration-150 hover:-translate-y-0.5 hover:border-brand-300 hover:shadow-lift' : ''}`}
      style={{ borderLeft: `3px solid ${color}`, flex: '1 1 150px', cursor: onClick ? 'pointer' : undefined, boxShadow: active ? `0 0 0 1px ${color}` : undefined }}
    >
      <div style={{ padding: '12px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ color, fontSize: 18 }}>{icon}</span>
          <div>
            <div style={{ fontSize: 12, color: '#888' }}>{title}</div>
            <div style={{ fontSize: 20, fontWeight: 600, color }}>{value}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AttendancePage() {
  const { token } = theme.useToken();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(7, 'day'), dayjs()]);
  const [anomalyOnly, setAnomalyOnly] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  const [openOnly, setOpenOnly] = useState(false); // Open-shift KPI drill-down (checked in, not out)
  const [view, setView] = useState<'list' | 'byday'>('list'); // flat list vs per-day summary
  const [status, setStatus] = useState<string | undefined>();
  const [userId, setUserId] = useState<string | undefined>();
  const [detailId, setDetailId] = useState<string | null>(null);
  const [userDrawer, setUserDrawer] = useState<AttUser | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  // After a leave range that touches today/future is marked for an SP who owns territories,
  // we chain into Assign-coverage pre-filled (skippable). Only for markers who can grant coverage.
  const [coveragePrompt, setCoveragePrompt] = useState<{ absentId: string; from: string; to: string } | null>(null);
  const canManage = usePermission('ATTENDANCE.MANAGE');
  const canCoverage = usePermission('TERRITORY.COVERAGE');
  const canApproveReg = usePermission('ATTENDANCE.REGULARIZE.APPROVE');
  const badges = useBadges(); // anomalies + pending regularizations, for the tab indicators

  // Fetch the whole window once (real rows of every status + synthetic absents). Status / anomaly /
  // open-shift / active filtering is done client-side below, so the KPI totals stay stable and every
  // card can drill the table without a refetch.
  const params = useMemo(() => {
    const p: Record<string, string> = {
      from: range[0].format('YYYY-MM-DD'),
      to: range[1].format('YYYY-MM-DD'),
      absent: '1',
    };
    if (userId) p.userId = userId;
    return p;
  }, [range, userId]);

  const { data, isLoading } = useQuery({
    queryKey: ['attendance', params],
    queryFn: () => request<any[]>({ url: '/attendance', params }),
    placeholderData: keepPreviousData,
  });

  const { data: users } = useQuery({
    queryKey: ['sales-users'],
    queryFn: () => request<any>({ url: '/users', params: { role: 'SALES_PERSON', pageSize: 200 } }),
  });
  // Holidays label weekly-off / holiday days in the per-day view.
  const { data: holidays } = useQuery({
    queryKey: ['holidays'],
    queryFn: () => request<any[]>({ url: '/holidays' }),
  });

  const rows = data ?? [];
  const kpis = useMemo(() => {
    // Real (stored) rows vs synthetic ABSENT rows the backend folds in. Present/Open/Anomaly count
    // only real records; Absent counts both admin-marked ABSENT and synthesized absences.
    const real = rows.filter((r: any) => !r.synthetic);
    const present = real.filter((r: any) => r.status === 'PRESENT').length;
    const anomalies = real.filter((r: any) => (r.anomalyFlags?.length ?? 0) > 0).length;
    const openShift = real.filter((r: any) => r.checkInAt && !r.checkOutAt).length;
    const absent = rows.filter((r: any) => r.status === 'ABSENT').length;
    return { records: real.length, present, absent, anomalies, openShift };
  }, [rows]);

  // Client-side filtering (the query already fetched the whole window). "Active only" shows worked days
  // (Present / Half-day); a status / open-shift / anomaly focus narrows further. Synthetic ABSENT rows
  // only surface in the Absent view.
  const tableRows = useMemo(() => {
    let recs = (data ?? []) as any[];
    if (activeOnly) recs = recs.filter((r) => r.status === 'PRESENT' || r.status === 'HALF_DAY');
    if (status) recs = recs.filter((r) => r.status === status);
    if (anomalyOnly) recs = recs.filter((r) => (r.anomalyFlags?.length ?? 0) > 0);
    if (openOnly) recs = recs.filter((r) => r.checkInAt && !r.checkOutAt);
    if (status !== 'ABSENT') recs = recs.filter((r) => !r.synthetic);
    return recs;
  }, [data, activeOnly, status, anomalyOnly, openOnly]);

  // KPI drill-down: each card toggles a table filter; the active card is ringed and clicking it clears.
  // Totals are computed from the full fetch above, so they don't change when a card is selected.
  const focus = {
    records: !activeOnly && !status && !anomalyOnly && !openOnly,
    present: status === 'PRESENT',
    absent: status === 'ABSENT',
    open: openOnly,
    anomaly: anomalyOnly,
  };
  const clearFocus = () => { setActiveOnly(true); setStatus(undefined); setAnomalyOnly(false); setOpenOnly(false); };
  // A KPI drill-down applies its filter AND drops to the flat list so the filtered rows are visible.
  const setFocus = (apply: () => void) => { setActiveOnly(false); setStatus(undefined); setAnomalyOnly(false); setOpenOnly(false); setView('list'); apply(); };

  // Per-day rollup: every date in the range (newest first, capped at today), with status counts and the
  // day's rep rows for the expander. Sundays and declared holidays are labelled and never marked absent.
  const dayGroups = useMemo(() => {
    if (view !== 'byday') return [];
    const recs = (data ?? []) as any[];
    const byDate = new Map<string, any[]>();
    for (const r of recs) { const a = byDate.get(r.logDate); if (a) a.push(r); else byDate.set(r.logDate, [r]); }
    const holName = new Map((holidays ?? []).map((h: any) => [String(h.holidayDate), h.name]));
    const start = range[0];
    const today = dayjs();
    let end = range[1];
    if (end.isAfter(today, 'day')) end = today; // no future rows to roll up
    const out: any[] = [];
    for (let d = end; !d.isBefore(start, 'day'); d = d.subtract(1, 'day')) {
      const ds = d.format('YYYY-MM-DD');
      const rows = (byDate.get(ds) ?? []).slice().sort((a, b) => String(a.staffName ?? '').localeCompare(String(b.staffName ?? '')));
      out.push({
        id: ds, date: ds, rows, count: rows.length,
        isSunday: d.day() === 0,
        holiday: holName.get(ds) ?? null,
        present: rows.filter((r) => r.status === 'PRESENT').length,
        absent: rows.filter((r) => r.status === 'ABSENT').length,
        halfDay: rows.filter((r) => r.status === 'HALF_DAY').length,
        leave: rows.filter((r) => r.status === 'LEAVE').length,
        anomalies: rows.filter((r) => (r.anomalyFlags?.length ?? 0) > 0).length,
      });
    }
    return out;
  }, [view, data, holidays, range]);

  const columns: DataTableColumn<any>[] = [
    { key: 'logDate', title: 'Date', dataIndex: 'logDate', sortable: true, alwaysVisible: true, width: 110 },
    {
      key: 'staff', title: 'Staff', sortable: true, alwaysVisible: true,
      sorter: (a, b) => String(a.staffName ?? '').localeCompare(String(b.staffName ?? '')),
      render: (_: any, r: any) => (
        <div>
          <div style={{ fontWeight: 500 }}>{r.staffName ?? `#${r.userId}`}</div>
          {r.staffRole && <div style={{ fontSize: 11, color: token.colorTextTertiary }}>{r.staffRole}</div>}
        </div>
      ),
    },
    { key: 'checkInAt', title: 'Check-in', dataIndex: 'checkInAt', sortable: true, render: (v: string) => v ? formatIstTime(v) : '—' },
    { key: 'checkOutAt', title: 'Check-out', dataIndex: 'checkOutAt', sortable: true, render: (v: string, r: any) => v ? formatIstTime(v) : (r.checkInAt ? <Tag tone="blue">Open</Tag> : '—') },
    { key: 'workedMin', title: 'Worked', dataIndex: 'workedMin', sortable: true, align: 'right' as const, render: (v: number) => fmtWorked(v) },
    { key: 'checkInFaceScore', title: 'Face', dataIndex: 'checkInFaceScore', sortable: true, defaultVisible: false, align: 'right' as const, render: (v: any) => v ?? '—' },
    {
      key: 'isWithinGeofence', title: 'Geofence', dataIndex: 'isWithinGeofence', sortable: true,
      render: (v: boolean, r: any) => !r.checkInAt ? '—' : v ? <Tag tone="green">In</Tag> : <Tag tone="red">Out</Tag>,
    },
    {
      // Where the rep last was that day — the latest activity (a punch or a visit), as a full address.
      key: 'lastLocation', title: 'Last location', dataIndex: 'lastActivityAt', width: 280, alwaysVisible: true,
      render: (_: any, r: any) => (
        <LastLocationCell
          lat={r.lastLat ?? r.checkOutLat ?? r.checkInLat}
          lng={r.lastLng ?? r.checkOutLong ?? r.checkInLong}
          type={r.lastActivityType ?? (r.checkOutAt ? 'Check-out' : r.checkInAt ? 'Check-in' : null)}
          at={r.lastActivityAt ?? r.checkOutAt ?? r.checkInAt}
        />
      ),
    },
    {
      key: 'status', title: 'Status', dataIndex: 'status', sortable: true,
      render: (v: string, r: any) => (
        <Space size={4} wrap>
          <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag>
          {r.source && SOURCE_TAG[r.source] && <Tag tone={SOURCE_TAG[r.source].color}>{SOURCE_TAG[r.source].text}</Tag>}
          {r.missingCheckout && <Tag tone="gold">Missing check-out</Tag>}
        </Space>
      ),
    },
    {
      key: 'anomalyFlags', title: 'Anomalies', dataIndex: 'anomalyFlags',
      render: (flags: string[] | undefined, r: any) => (
        <div className="flex items-center gap-1">
          {r.synthetic ? <span className="text-muted">—</span>
            : (flags ?? []).length > 0
              ? (flags ?? []).map((f) => <TwTag key={f} tone="red">{f.replace(/_/g, ' ')}</TwTag>)
              : <TwTag tone="green">OK</TwTag>}
          <TwTooltip label="Attendance history">
            <IconButton
              diameter={26}
              className="text-muted hover:bg-surface-2 hover:text-content [&>svg]:size-4"
              onClick={() => setUserDrawer({ id: String(r.userId), name: r.staffName, role: r.staffRole })}
            >
              <CalendarClock />
            </IconButton>
          </TwTooltip>
        </div>
      ),
    },
    {
      key: 'actions', title: '', alwaysVisible: true, width: 50,
      // Synthetic ABSENT rows have no stored record → nothing to open.
      render: (_: any, r: any) => r.synthetic ? null : (
        <Tooltip label="View details">
          <Button variant="text" size="sm" aria-label="View details" icon={<Eye size={16} />} onClick={() => setDetailId(String(r.id))} />
        </Tooltip>
      ),
    },
  ];

  // Filters shared by the flat list and the per-day view.
  const rangePicker = (
    <DatePicker.RangePicker disabledDate={disableFuture} value={range} onChange={(r) => r && r[0] && r[1] && setRange([r[0], r[1]])} allowClear={false} />
  );
  const staffPicker = (
    <Select
      placeholder="All staff" allowClear showSearch optionFilterProp="label"
      style={{ width: 220 }} value={userId} onChange={setUserId}
      options={(users?.data ?? []).map((u: any) => ({ value: String(u.id), label: u.fullName }))}
    />
  );
  const viewToggle = (
    <Segmented
      value={view} onChange={(v) => setView(v as 'list' | 'byday')}
      options={[{ label: 'List', value: 'list' }, { label: 'By day', value: 'byday' }]}
    />
  );
  const markBtn = canManage ? (
    <Button variant="primary" size="sm" icon={<Plus size={16} />} onClick={() => setCreateOpen(true)}>Mark attendance</Button>
  ) : undefined;

  // Chain into coverage only when the leave reaches today/future AND the SP holds territories AND
  // the marker can actually grant coverage. Coverage starts no earlier than today (can't cover the past).
  const handleLeaveMarked = (res: any, ctx: { userId: string; from: string; to: string }) => {
    if (!canCoverage || !res?.touchesTodayOrFuture || (res?.territoryCount ?? 0) === 0) return;
    const todayS = dayjs().format('YYYY-MM-DD');
    setCoveragePrompt({ absentId: String(ctx.userId), from: ctx.from < todayS ? todayS : ctx.from, to: ctx.to });
  };

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center gap-3">
        <Typography.Title level={4} style={{ margin: 0 }}>Attendance</Typography.Title>
        <div className="ml-auto flex flex-wrap items-center gap-2">{markBtn}</div>
      </div>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Daily check-in / check-out with GPS, selfie and anomaly detection.
      </Typography.Paragraph>

      <Tabs
        items={[
          {
            key: 'log',
            label: (
              <Space size={6}>
                Attendance log
                <Tooltip label="Anomalies + incomplete entries (missing check-in/out) needing review">
                  <Badge count={(badges.attendanceAnomalies ?? 0) + (badges.attendanceIncomplete ?? 0)} size="small" overflowCount={99} />
                </Tooltip>
              </Space>
            ),
            children: (
              <>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
                  <Kpi
                    title="Records" value={kpis.records} color={token.colorPrimary} icon={<Users size={16} />}
                    active={focus.records} onClick={() => (focus.records ? clearFocus() : setFocus(() => {}))}
                  />
                  <Kpi
                    title="Present" value={kpis.present} color="#1F8A4C" icon={<CheckCircle2 size={16} />}
                    active={focus.present} onClick={() => (focus.present ? clearFocus() : setFocus(() => setStatus('PRESENT')))}
                  />
                  <Kpi
                    title="Absent" value={kpis.absent} color="#CF1322" icon={<UserX size={16} />}
                    active={focus.absent} onClick={() => (focus.absent ? clearFocus() : setFocus(() => setStatus('ABSENT')))}
                  />
                  <Kpi
                    title="Open shift" value={kpis.openShift} color="#1677FF" icon={<Clock size={16} />}
                    active={focus.open} onClick={() => (focus.open ? clearFocus() : setFocus(() => setOpenOnly(true)))}
                  />
                  <Kpi
                    title="Anomalies" value={kpis.anomalies} color="#FA8C16" icon={<AlertTriangle size={16} />}
                    active={focus.anomaly} onClick={() => (focus.anomaly ? clearFocus() : setFocus(() => setAnomalyOnly(true)))}
                  />
                </div>
                {view === 'list' ? (
                  <Card>
                    <DataTable
                      storageKey="attendance"
                      rowKey="id"
                      loading={isLoading}
                      dataSource={tableRows}
                      columns={columns}
                      pagination={{ pageSize: 25, showSizeChanger: true }}
                      toolbarLeft={
                        <>
                          {viewToggle}
                          {rangePicker}
                          {staffPicker}
                          <Select
                            placeholder="Any status" allowClear disabled={activeOnly} style={{ width: 140 }} value={status}
                            onChange={(v) => (v ? setFocus(() => setStatus(v)) : clearFocus())}
                            options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                          />
                          <Checkbox checked={activeOnly} onCheckedChange={(checked) => (checked ? clearFocus() : setFocus(() => {}))} label="Active only" />
                          <Space>
                            <Switch checked={anomalyOnly} onCheckedChange={(v) => (v ? setFocus(() => setAnomalyOnly(true)) : clearFocus())} />
                            <span>Anomalies only</span>
                          </Space>
                        </>
                      }
                    />
                  </Card>
                ) : (
                  <Card>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 12 }}>
                      {viewToggle}
                      {rangePicker}
                      {staffPicker}
                    </div>
                    <DailySummary groups={dayGroups} loading={isLoading} onOpen={(id) => setDetailId(id)} />
                  </Card>
                )}
              </>
            ),
          },
          ...(canApproveReg ? [{
            key: 'reg',
            label: <Space size={6}><RefreshCw size={16} /> Regularization<Badge count={badges.regularizationsPending ?? 0} size="small" overflowCount={99} /></Space>,
            children: <RegularizationReviewCard />,
          }] : []),
          ...(canManage ? [{
            key: 'holidays',
            label: <Space size={6}><CalendarDays size={16} /> Holidays</Space>,
            children: <HolidaysCard />,
          }] : []),
        ]}
      />

      <AttendanceDetailDrawer id={detailId} onClose={() => setDetailId(null)} canManage={canManage} />
      <AttendanceUserDrawer user={userDrawer} onClose={() => setUserDrawer(null)} />
      <MarkAttendanceModal open={createOpen} onClose={() => setCreateOpen(false)} users={users?.data ?? []} onLeaveMarked={handleLeaveMarked} />
      <GrantCoverageModal
        open={!!coveragePrompt}
        onClose={() => setCoveragePrompt(null)}
        presetAbsentId={coveragePrompt?.absentId}
        presetValidFrom={coveragePrompt?.from}
        presetValidTo={coveragePrompt?.to}
      />
    </div>
  );
}

/** Per-day rollup: one row per date with status counts; expand a day to see its rep rows. */
function DailySummary({ groups, loading, onOpen }: { groups: any[]; loading: boolean; onOpen: (id: string) => void }) {
  const dayTag = (g: any) =>
    g.holiday ? <Tag tone="gold">Holiday · {g.holiday}</Tag>
      : g.isSunday ? <Tag>Weekly off</Tag>
        : <Tag tone="green">Working</Tag>;
  const num = (v: number, color?: string) =>
    v ? <span style={{ fontWeight: 600, color }}>{v}</span> : <span style={{ color: '#bbb' }}>—</span>;

  return (
    <Table
      size="small"
      rowKey="id"
      loading={loading}
      dataSource={groups}
      pagination={{ pageSize: 31, hideOnSinglePage: true }}
      locale={{ emptyText: <EmptyState title="No days in range" /> }}
      columns={[
        { title: 'Date', dataIndex: 'date', render: (v: string) => <span style={{ fontWeight: 500 }}>{dayjs(v).format('ddd, DD MMM YYYY')}</span> },
        { title: 'Day', key: 'day', render: (_: any, g: any) => dayTag(g) },
        { title: 'Present', dataIndex: 'present', align: 'right' as const, render: (v: number) => num(v, '#1F8A4C') },
        { title: 'Absent', dataIndex: 'absent', align: 'right' as const, render: (v: number) => num(v, '#CF1322') },
        { title: 'Half-day', dataIndex: 'halfDay', align: 'right' as const, render: (v: number) => num(v, '#B8860B') },
        { title: 'Leave', dataIndex: 'leave', align: 'right' as const, render: (v: number) => num(v, '#1677FF') },
        { title: 'Anomalies', dataIndex: 'anomalies', align: 'right' as const, render: (v: number) => num(v, '#FA8C16') },
      ]}
      expandable={{
        rowExpandable: (g: any) => g.count > 0,
        expandedRowRender: (g: any) => (
          <Table
            size="small"
            rowKey="id"
            pagination={false}
            dataSource={g.rows}
            columns={[
              {
                title: 'Staff', dataIndex: 'staffName',
                render: (v: string, r: any) => (
                  <div>
                    <div style={{ fontWeight: 500 }}>{v ?? `#${r.userId}`}</div>
                    {r.staffRole && <div style={{ fontSize: 11, color: '#999' }}>{r.staffRole}</div>}
                  </div>
                ),
              },
              { title: 'Check-in', dataIndex: 'checkInAt', render: (v: string) => (v ? formatIstTime(v) : '—') },
              { title: 'Check-out', dataIndex: 'checkOutAt', render: (v: string, r: any) => (v ? formatIstTime(v) : r.checkInAt ? <Tag tone="blue">Open</Tag> : '—') },
              { title: 'Worked', dataIndex: 'workedMin', align: 'right' as const, render: (v: number) => fmtWorked(v) },
              {
                title: 'Status', dataIndex: 'status',
                render: (v: string, r: any) => (
                  <Space size={4} wrap>
                    <Tag tone={STATUS_COLOR[v] ?? 'neutral'}>{v}</Tag>
                    {r.source && SOURCE_TAG[r.source] && <Tag tone={SOURCE_TAG[r.source].color}>{SOURCE_TAG[r.source].text}</Tag>}
                  </Space>
                ),
              },
              { title: '', width: 44, render: (_: any, r: any) => (r.synthetic ? null : <Button variant="text" size="sm" aria-label="View details" icon={<Eye size={16} />} onClick={() => onOpen(String(r.id))} />) },
            ]}
          />
        ),
      }}
    />
  );
}

function AttendanceDetailDrawer({ id, onClose, canManage }: { id: string | null; onClose: () => void; canManage: boolean }) {
  const qc = useQueryClient();
  const { data: a, isLoading } = useQuery({
    queryKey: ['attendance-detail', id],
    queryFn: () => request<any>({ url: `/attendance/${id}` }),
    enabled: !!id,
  });
  const [newStatus, setNewStatus] = useState<string | undefined>();

  // ── Last location: the rep's latest activity that day (a punch OR a visit) with coordinates. ──
  const lastLat = a?.lastLat ?? a?.checkOutLat ?? a?.checkInLat;
  const lastLng = a?.lastLng ?? a?.checkOutLong ?? a?.checkInLong;
  const lastAt = a?.lastActivityAt ?? a?.checkOutAt ?? a?.checkInAt;
  const lastType = a?.lastActivityType ?? (a?.checkOutAt ? 'Check-out' : a?.checkInAt ? 'Check-in' : null);
  const lastLoc = useReverseGeocode(lastLat, lastLng); // reverse-geocoded full address (cached)

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['attendance-detail', id] });
    qc.invalidateQueries({ queryKey: ['attendance'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };
  const setStatusMut = useMutation({
    mutationFn: (s: string) => request({ url: `/attendance/${id}/status`, method: 'PATCH', data: { status: s } }),
    onSuccess: () => { message.success('Status updated'); setNewStatus(undefined); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  const clearMut = useMutation({
    mutationFn: () => request({ url: `/attendance/${id}/clear-anomalies`, method: 'PATCH', data: {} }),
    onSuccess: () => { message.success('Anomalies cleared'); refresh(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });

  const Geo = ({ lat, lng, label }: { lat?: string | null; lng?: string | null; label: string }) => {
    const url = mapsUrl(lat, lng);
    if (!url) return <span>—</span>;
    return (
      <a href={url} target="_blank" rel="noreferrer">
        <MapPin size={16} /> {label} ({Number(lat).toFixed(4)}, {Number(lng).toFixed(4)})
      </a>
    );
  };

  return (
    <Drawer open={!!id} onClose={onClose} width={460} title={a ? `${a.staffName ?? `#${a.userId}`} · ${a.logDate}` : 'Attendance'}>
      {isLoading || !a ? null : (
        <>
          <Descriptions bordered size="small" column={1}>
            <Descriptions.Item label="Staff">{a.staffName ?? `#${a.userId}`}{a.staffRole ? ` · ${a.staffRole}` : ''}</Descriptions.Item>
            <Descriptions.Item label="Status"><Tag tone={STATUS_COLOR[a.status] ?? 'neutral'}>{a.status}</Tag></Descriptions.Item>
            <Descriptions.Item label="Check-in">{a.checkInAt ? formatIst(a.checkInAt) : '—'}</Descriptions.Item>
            <Descriptions.Item label="Check-out">{a.checkOutAt ? formatIst(a.checkOutAt) : 'Open'}</Descriptions.Item>
            <Descriptions.Item label="Worked">{fmtWorked(a.workedMin)}</Descriptions.Item>
            <Descriptions.Item label="Face score">{a.checkInFaceScore ?? '—'}</Descriptions.Item>
            <Descriptions.Item label="Geofence">{a.isWithinGeofence ? <Tag tone="green">Inside</Tag> : <Tag tone="red">Outside</Tag>}</Descriptions.Item>
            <Descriptions.Item label="Mock location">{a.isMockLocation ? <Tag tone="red">Yes</Tag> : <Tag tone="green">No</Tag>}</Descriptions.Item>
            {lastLat && lastLng && (
              <Descriptions.Item label="Last location">
                <div className="space-y-1.5">
                  <div className="text-content">
                    {lastLoc.isFetching
                      ? <span className="text-muted">Resolving address…</span>
                      : (lastLoc.data || `lat ${Number(lastLat).toFixed(5)}, lng ${Number(lastLng).toFixed(5)}`)}
                  </div>
                  <div className="text-xs text-muted">{lastType ?? 'Location'}{lastAt ? ` · ${formatIst(lastAt)}` : ''}</div>
                  <Button variant="ghost" size="sm" icon={<MapPin size={14} />} href={mapsUrl(lastLat, lastLng)} target="_blank" rel="noreferrer">
                    View in map
                  </Button>
                </div>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="Check-in location"><Geo lat={a.checkInLat} lng={a.checkInLong} label="Map" /></Descriptions.Item>
            <Descriptions.Item label="Check-out location"><Geo lat={a.checkOutLat} lng={a.checkOutLong} label="Map" /></Descriptions.Item>
            <Descriptions.Item label="Anomalies">
              {(a.anomalyFlags ?? []).length ? (a.anomalyFlags ?? []).map((f: string) => <Tag tone="red" key={f}>{f.replace(/_/g, ' ')}</Tag>) : <Tag tone="green">None</Tag>}
            </Descriptions.Item>
          </Descriptions>

          <Typography.Text strong style={{ display: 'block', margin: '16px 0 8px' }}>Selfies</Typography.Text>
          {a.checkInSelfieUrl || a.checkOutSelfieUrl ? (
            <Image.PreviewGroup>
              <Space>
                {a.checkInSelfieUrl && (
                  <div style={{ textAlign: 'center' }}>
                    <Image src={a.checkInSelfieUrl} width={120} height={120} style={{ objectFit: 'cover', borderRadius: 8 }} />
                    <div style={{ fontSize: 11, color: '#888' }}>Check-in</div>
                  </div>
                )}
                {a.checkOutSelfieUrl && (
                  <div style={{ textAlign: 'center' }}>
                    <Image src={a.checkOutSelfieUrl} width={120} height={120} style={{ objectFit: 'cover', borderRadius: 8 }} />
                    <div style={{ fontSize: 11, color: '#888' }}>Check-out</div>
                  </div>
                )}
              </Space>
            </Image.PreviewGroup>
          ) : (
            <EmptyState title="No selfie captured" />
          )}

          {canManage && (
            <>
              <Typography.Text strong style={{ display: 'block', margin: '18px 0 8px' }}>Admin actions</Typography.Text>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div className="flex w-full gap-2">
                  <Select
                    style={{ flex: 1 }} placeholder="Override status…"
                    value={newStatus ?? a.status}
                    onChange={setNewStatus}
                    options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))}
                  />
                  <Button
                    variant="primary" loading={setStatusMut.isPending}
                    disabled={!newStatus || newStatus === a.status}
                    onClick={() => newStatus && setStatusMut.mutate(newStatus)}
                  >Save</Button>
                </div>
                {(a.anomalyFlags ?? []).length > 0 && (
                  <Popconfirm title="Clear anomaly flags?" description="Marks this record as reviewed." onConfirm={() => clearMut.mutate()}>
                    <Button variant="danger" block loading={clearMut.isPending} icon={<AlertTriangle size={16} />}>Clear anomalies</Button>
                  </Popconfirm>
                )}
              </Space>
            </>
          )}
        </>
      )}
    </Drawer>
  );
}

function MarkAttendanceModal({ open, onClose, users, onLeaveMarked }: {
  open: boolean; onClose: () => void; users: any[];
  onLeaveMarked?: (res: any, ctx: { userId: string; from: string; to: string }) => void;
}) {
  const qc = useQueryClient();
  const [form] = Form.useForm();
  const statusVal = Form.useWatch('status', form);
  const isLeave = statusVal === 'LEAVE';

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['attendance'] });
    qc.invalidateQueries({ queryKey: ['badges'] });
  };

  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: '/attendance/admin', method: 'POST',
      data: {
        userId: vals.userId,
        logDate: vals.logDate.format('YYYY-MM-DD'),
        status: vals.status,
        checkInAt: vals.checkInAt ? vals.checkInAt.toISOString() : undefined,
        checkOutAt: vals.checkOutAt ? vals.checkOutAt.toISOString() : undefined,
        note: vals.note,
      },
    }),
    onSuccess: () => {
      message.success('Attendance saved');
      invalidate();
      form.resetFields();
      onClose();
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to save'),
  });

  // LEAVE is marked across a range (future allowed); the server skips Sundays + applicable holidays.
  const leaveMut = useMutation({
    mutationFn: (body: { userId: string; from: string; to: string; note?: string }) =>
      request<any>({ url: '/attendance/admin/leave', method: 'POST', data: body }),
    onSuccess: (res: any, body) => {
      const w = res?.written?.length ?? 0;
      const sk = res?.skipped?.length ?? 0;
      const ov = res?.overwritten?.length ?? 0;
      message.success(
        w ? `Marked leave: ${w} day${w === 1 ? '' : 's'}` + (sk ? `, skipped ${sk} (Sunday/holiday)` : '') + (ov ? `, overwrote ${ov}` : '')
          : 'No working days in that range — nothing marked (all Sundays/holidays).',
      );
      invalidate();
      form.resetFields();
      onClose();
      onLeaveMarked?.(res, body);
    },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed to mark leave'),
  });

  const onFinish = async (vals: any) => {
    if (vals.status !== 'LEAVE') { save.mutate(vals); return; }
    const rng = vals.leaveRange as [Dayjs | null, Dayjs | null] | undefined;
    if (!rng || !rng[0] || !rng[1]) { message.error('Pick a valid leave date range (both start and end).'); return; }
    const [from, to] = rng;
    const fromS = from.format('YYYY-MM-DD');
    const toS = to.format('YYYY-MM-DD');
    // Warn-but-allow when the range overwrites existing non-leave attendance.
    let clashes: any[] = [];
    try {
      const existing = await request<any[]>({ url: '/attendance', params: { userId: vals.userId, from: fromS, to: toS } });
      clashes = (existing ?? []).filter((r) => !r.synthetic && r.status !== 'LEAVE');
    } catch { /* pre-check is best-effort; the server still records overwrites */ }
    if (clashes.length) {
      const dates = clashes.slice(0, 6).map((c) => c.logDate).join(', ');
      const ok = await new Promise<boolean>((resolve) => {
        Modal.confirm({
          title: 'Overwrite existing attendance?',
          content: `${clashes.length} day${clashes.length === 1 ? '' : 's'} in this range already have attendance (${dates}${clashes.length > 6 ? '…' : ''}). Marking leave will overwrite ${clashes.length === 1 ? 'it' : 'them'}.`,
          okText: 'Overwrite & mark leave', okButtonProps: { danger: true }, cancelText: 'Cancel',
          onOk: () => resolve(true), onCancel: () => resolve(false),
        });
      });
      if (!ok) return;
    }
    leaveMut.mutate({ userId: vals.userId, from: fromS, to: toS, note: vals.note });
  };

  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Mark attendance (on behalf)"
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending || leaveMut.isPending} onClick={() => form.submit()}>{isLeave ? 'Mark leave' : 'Save'}</Button></>}>
      <Form form={form} layout="vertical" onFinish={onFinish}
        initialValues={{ logDate: dayjs(), status: 'PRESENT' }}>
        <Form.Item name="userId" label="Staff" rules={[{ required: true, message: 'Pick a staff member' }]}>
          <Select showSearch optionFilterProp="label" placeholder="Select staff"
            options={users.map((u: any) => ({ value: String(u.id), label: u.fullName }))} />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select options={STATUS_OPTIONS.map((s) => ({ value: s, label: s }))} />
        </Form.Item>
        {isLeave ? (
          <Form.Item name="leaveRange" label="Leave period" rules={[{ required: true, message: 'Pick the leave date range' }]}
            extra="Sundays and holidays in the range are skipped automatically. Future dates allowed.">
            <DatePicker.RangePicker className="w-full" format="DD MMM YYYY" />
          </Form.Item>
        ) : (
          <>
            <Form.Item name="logDate" label="Date" rules={[{ required: true }]}>
              <DatePicker className="w-full" disabledDate={(d) => d && d.isAfter(dayjs(), 'day')} />
            </Form.Item>
            <Row gutter={12}>
              <Col span={12}><Form.Item name="checkInAt" label="Check-in (optional)"><DatePicker showTime format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
              <Col span={12}><Form.Item name="checkOutAt" label="Check-out (optional)"><DatePicker showTime format="DD MMM YYYY hh:mm A" className="w-full" /></Form.Item></Col>
            </Row>
          </>
        )}
        <Form.Item name="note" label="Reason / note"><Input.TextArea rows={2} placeholder="e.g. Approved leave, manual correction…" /></Form.Item>
      </Form>
    </Modal>
  );
}
